import { InlineMarkupType, MessageFormatType } from 'src/generated/graphql';
import { NO_VALUE } from 'src/features/string-group-detail/utils/versionDiff';
import { inlineMarkupLabel, messageFormatLabel } from 'src/common/formatLabels';
import { joinList } from 'src/common/utils/joinList';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/**
 * Field-level diff for a single string between two CONSECUTIVE versions of its
 * group — a version and the one immediately before it in the version list.
 *
 * `computeVersionDiff` in the string-group-detail feature answers a different
 * question — which strings in a group were added/edited/removed — and reports a
 * whole string as `changed` without saying which field moved. The string detail
 * page needs the per-field answer — for its source text, context note, inline
 * markup, message format and CX variants — so it compares them directly and
 * turns the result into a human sentence with {@link describeVersionChange}.
 */
export interface FieldDiff {
  /** True when the two versions hold different text. */
  changed: boolean;
  /** The PREDECESSOR version's value, normalized to '' when absent. */
  previous: string;
  /** THIS version's value, normalized to '' when absent. */
  value: string;
}

/**
 * Compares one text field between a version and its predecessor, so `changed`
 * means "this version changed the field", not "it differs from today".
 *
 * Null and undefined are normalized to '' so "field absent" and "field empty"
 * do not read as a change.
 */
export function diffTextField(
  previous: string | null | undefined,
  value: string | null | undefined,
): FieldDiff {
  const previousValue = previous ?? '';
  const thisValue = value ?? '';
  return {
    changed: previousValue !== thisValue,
    previous: previousValue,
    value: thisValue,
  };
}

/**
 * The group's CX ID membership at a version and its predecessor. CX IDs are
 * group-level metadata (which storefronts the group targets), not a per-string
 * field — but a CX ID change bumps the group version WITHOUT necessarily
 * touching this string's text. Diffing them here is what stops a CX-ID-only
 * version from reading as "No change" on the string detail page while the
 * string-group page's diff (see `computeCxIdDiff`) shows the change.
 */
export interface CxIdsDiff {
  /** True when the two versions target a different SET of CX IDs. */
  changed: boolean;
  /** The PREDECESSOR version's CX IDs, de-duplicated in first-seen order. */
  previous: string[];
  /** THIS version's CX IDs, de-duplicated in first-seen order. */
  value: string[];
}

/**
 * Compares the group's CX ID membership between a version and its predecessor.
 *
 * Membership is a set, so `changed` ignores ordering and duplicates — only a
 * genuine add or removal counts, matching `computeCxIdDiff` on the string-group
 * page. The de-duplicated lists are returned so the Diff modal can show the full
 * membership on each side rather than only the delta.
 */
export function diffCxIds(
  previous: readonly string[],
  value: readonly string[],
): CxIdsDiff {
  const previousUnique = [...new Set(previous)];
  const valueUnique = [...new Set(value)];
  const valueSet = new Set(valueUnique);
  const changed =
    previousUnique.length !== valueUnique.length ||
    previousUnique.some((cxId) => !valueSet.has(cxId));
  return { changed, previous: previousUnique, value: valueUnique };
}

/**
 * Picks which of the group's ACTUAL versions to show, newest first and capped at
 * `limit`. The current version and every earlier version are listed (the current
 * version appears as the top row, diffed against its predecessor — matching the
 * string-group detail page's history table); each row is compared with the next
 * one down in this same list. Versions NEWER than `currentVersion` are excluded:
 * a stale list can report them, but the string is only at `currentVersion`.
 *
 * The versions are supplied by `listStringGroupVersions` rather than derived as
 * N … 1: group versions are not guaranteed to be a contiguous range, so
 * synthesizing them both requests versions that may not exist and scales the
 * request count with the version number.
 *
 * The API returns the current version as a string; a value that is not a
 * positive integer yields an empty list rather than a bad query, so the panel
 * degrades to its empty state instead of firing nonsense requests.
 */
export function versionsToCompare(
  versions: readonly number[],
  currentVersion: string,
  limit: number,
): number[] {
  const parsed = parseInt(currentVersion, 10);
  if (Number.isNaN(parsed)) {
    return [];
  }
  return versions
    .filter((version) => version <= parsed)
    .sort((a, b) => b - a)
    .slice(0, limit);
}

// Enum → label maps mirror the option labels in the Create/Edit string modals
// (and the group diff table / view modal) so the diff and its summary read the
// same words the editor showed.

/**
 * Compares an enum field (inline markup, message format) between a version and
 * its predecessor. Both sides are rendered as the labels the editor showed; an
 * absent value (the backend reported UNKNOWN) normalizes to '' so it reads as
 * the shared placeholder in the diff table rather than as a change.
 */
function diffEnumField<E extends string>(
  previous: E | null | undefined,
  value: E | null | undefined,
  label: (value: E) => string,
): FieldDiff {
  return {
    changed: (previous ?? '') !== (value ?? ''),
    previous: previous ? label(previous) : '',
    value: value ? label(value) : '',
  };
}

/** Inline markup change between a version and its predecessor, label-rendered. */
export function diffInlineMarkup(
  t: TranslateFn,
  previous: InlineMarkupType | null | undefined,
  value: InlineMarkupType | null | undefined,
): FieldDiff {
  return diffEnumField(previous, value, (v) => inlineMarkupLabel(t, v));
}

/** Message format change between a version and its predecessor, label-rendered. */
export function diffMessageFormat(
  t: TranslateFn,
  previous: MessageFormatType | null | undefined,
  value: MessageFormatType | null | undefined,
): FieldDiff {
  return diffEnumField(previous, value, (v) => messageFormatLabel(t, v));
}

/** One CX variant override as recorded at a single version. */
export interface VariantSnapshot {
  cxId: string;
  sourceText: string;
  contextNote?: string | null;
}

/**
 * The change to a string's CX variant overrides between a version and its
 * predecessor. Counts drive the one-line summary ("1 variant removed"); `fields`
 * carries the per-variant, per-property rows the Diff modal renders — in the
 * same {field, previous, current} shape the shared VersionDiffTable consumes.
 */
export interface VariantsDiff {
  changed: boolean;
  /** Variants present in this version but not its predecessor. */
  added: number;
  /** Variants present in the predecessor but dropped in this version. */
  removed: number;
  /** Variants present in both whose text or context note changed. */
  modified: number;
  fields: { field: string; previous: string; current: string }[];
}

/** Renders a variant text value, falling back to the shared placeholder. */
function variantValue(value: string | null | undefined): string {
  return value ? value : NO_VALUE;
}

/**
 * The rows for a variant that appeared or disappeared whole. One row per
 * property the variant actually has (source text, context note), so a
 * context-note-only override is still visible and no property renders as a
 * misleading `— → —`. The absent side is always the placeholder; `side` picks
 * which column that is. A variant with no populated property at all (not
 * reachable through the editor, which requires source text) still yields one
 * source-text row so the add/removal the summary counts is never invisible.
 */
function variantAddRemoveRows(
  fieldLabel: (property: string) => string,
  t: TranslateFn,
  variant: VariantSnapshot,
  side: 'previous' | 'current',
): VariantsDiff['fields'] {
  const row = (property: string, value: string | null | undefined) => ({
    field: fieldLabel(property),
    previous: side === 'previous' ? variantValue(value) : NO_VALUE,
    current: side === 'current' ? variantValue(value) : NO_VALUE,
  });

  const rows: VariantsDiff['fields'] = [];
  if (variant.sourceText) {
    rows.push(row(t('string-field-source-text'), variant.sourceText));
  }
  if (variant.contextNote) {
    rows.push(row(t('string-field-context-note'), variant.contextNote));
  }
  return rows.length > 0
    ? rows
    : [row(t('string-field-source-text'), variant.sourceText)];
}

/**
 * Diffs a string's CX variant overrides by cxId between a version and its
 * predecessor. A variant on only one side is an add/remove — listed with a row
 * per property it carries (source text, context note); one on both sides is
 * "modified" and contributes a row per differing property. cxIds are visited in
 * a stable sorted order so the rows and counts are deterministic regardless of
 * the backend's variant ordering. Mirrors the group page's `computeVariantDiffs`
 * so both diff surfaces describe variants alike.
 */
export function diffVariants(
  t: TranslateFn,
  previous: readonly VariantSnapshot[],
  value: readonly VariantSnapshot[],
): VariantsDiff {
  const previousByCx = new Map(previous.map((v) => [v.cxId, v]));
  const valueByCx = new Map(value.map((v) => [v.cxId, v]));
  const cxIds = [
    ...new Set([...previousByCx.keys(), ...valueByCx.keys()]),
  ].sort((a, b) => a.localeCompare(b));

  let added = 0;
  let removed = 0;
  let modified = 0;
  const fields: VariantsDiff['fields'] = [];

  for (const cxId of cxIds) {
    const before = previousByCx.get(cxId);
    const after = valueByCx.get(cxId);
    // One resource composing all three parts; mirrors the group page's `computeVariantDiffs`.
    const fieldLabel = (property: string): string =>
      t('sgd-diff-variant-field', { cxId, property });

    if (before && !after) {
      removed += 1;
      fields.push(...variantAddRemoveRows(fieldLabel, t, before, 'previous'));
      continue;
    }
    if (!before && after) {
      added += 1;
      fields.push(...variantAddRemoveRows(fieldLabel, t, after, 'current'));
      continue;
    }
    if (before && after) {
      let modifiedHere = false;
      if (before.sourceText !== after.sourceText) {
        modifiedHere = true;
        fields.push({
          field: fieldLabel(t('string-field-source-text')),
          previous: variantValue(before.sourceText),
          current: variantValue(after.sourceText),
        });
      }
      if ((before.contextNote ?? '') !== (after.contextNote ?? '')) {
        modifiedHere = true;
        fields.push({
          field: fieldLabel(t('string-field-context-note')),
          previous: variantValue(before.contextNote),
          current: variantValue(after.contextNote),
        });
      }
      if (modifiedHere) {
        modified += 1;
      }
    }
  }

  return {
    changed: added + removed + modified > 0,
    added,
    removed,
    modified,
    fields,
  };
}

/**
 * The per-field/per-category change flags a single version's descriptive
 * summary is built from. Each maps to one clause in {@link describeVersionChange}.
 */
export interface VersionChangeInputs {
  sourceTextChanged: boolean;
  contextNoteChanged: boolean;
  inlineMarkupChanged: boolean;
  messageFormatChanged: boolean;
  variantsAdded: number;
  variantsRemoved: number;
  variantsModified: number;
  cxIdsChanged: boolean;
}

// The order fields are listed in the summary — matching the Edit string modal's
// layout and the mock, so a reader scans the same sequence everywhere.
const FIELD_CLAUSE_ORDER: [keyof VersionChangeInputs, string][] = [
  ['sourceTextChanged', 'string-field-source-text'],
  ['contextNoteChanged', 'string-field-context-note'],
  ['inlineMarkupChanged', 'string-field-inline-markup'],
  ['messageFormatChanged', 'string-field-message-format'],
];

/** The `edited` key's id still ends `-changed`: the copy was reworded but the id is pinned by the
 * committed packs, which `arb-tools compile` validates the root bundle against. */
const VARIANT_CLAUSE_IDS = {
  added: 'string-change-variants-added',
  removed: 'string-change-variants-removed',
  edited: 'string-change-variants-changed',
} as const;

/** "N variants <verb>", or null when the count is zero. */
function variantClause(
  t: TranslateFn,
  count: number,
  kind: keyof typeof VARIANT_CLAUSE_IDS,
): string | null {
  if (count <= 0) {
    return null;
  }
  return t(VARIANT_CLAUSE_IDS[kind], { count });
}

/**
 * Turns a version's change flags into the human sentence shown in the Version
 * History "Change" column — e.g. "Source text and Inline markup edited" or
 * "Source text, Context note, Inline markup, and Message format edited, 1
 * variant removed, and CX ID edited". Returns "No change" when nothing moved,
 * so a caller can use it directly for any diffed row.
 */
export function describeVersionChange(
  t: TranslateFn,
  locale: string,
  input: VersionChangeInputs,
): string {
  const changedFields = FIELD_CLAUSE_ORDER.filter(([key]) => input[key]).map(
    ([, labelId]) => t(labelId),
  );

  const clauses: string[] = [];
  if (changedFields.length > 0) {
    clauses.push(
      t('string-change-fields-changed', {
        fields: joinList(locale, changedFields),
      }),
    );
  }
  for (const clause of [
    variantClause(t, input.variantsAdded, 'added'),
    variantClause(t, input.variantsRemoved, 'removed'),
    variantClause(t, input.variantsModified, 'edited'),
  ]) {
    if (clause) {
      clauses.push(clause);
    }
  }
  // Singular "CX ID" regardless of count, matching the group page and the mock.
  if (input.cxIdsChanged) {
    clauses.push(t('string-change-cxid'));
  }

  return clauses.length > 0
    ? joinList(locale, clauses)
    : t('string-change-none');
}
